import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { BaseLayoutService } from '@app/global/services/base-layout.service';
import { AiReportTypeEnum, MenuItem, TabletApp, TokenService, DraggableSnapLockAxis } from '@common';
import { CheckQuestionService } from '@shared/service/check-question.service';
import { MessageApiService } from '@app/message/message.api.service';
import { QuestionHandleService } from '@shared/service/question-handle.service';
import { SharedService } from '@shared/service/shared.service';
import { Site, SiteService } from '@shared/service/site.service';
import { NzModalService } from 'ng-zorro-antd/modal';
import { fromEvent, Observable, Subscription } from 'rxjs';
import { LoginTypeEnum } from '@shared/service/auth.service';
import { PermissionCode } from '@shared/model/auth.models';

/**
 * 应用根组件
 *
 * @since 1.0
 * @version 1.0
 * @change 添加监听路由，如果为阅卷页面则取消全局页头。
 */
@Component({
  selector: 'fl-base-layout',
  templateUrl: './base-layout.component.html',
  styleUrls: ['./base-layout.component.scss'],
  standalone: false,
})
export class BaseLayoutComponent implements OnInit, OnDestroy {
  title = '辅立码课';
  site: Site;

  logo = '';

  // 搜索
  searchValue: string;
  searchModel = 'course';
  searchModelMap = new Map([
    ['course', '课程'],
    ['question', '题目'],
    ['paper', '试卷'],
    ['chapter', '知识点'],
    ['task', '任务'],
  ]);

  // 是否是竹山中学
  isZhushanSchool = false;

  // 选题篮子 since 19.3.3.1
  selectQuestionCount = 0;

  // 通知 since 19.4.2
  noticeCount = 0;

  // 消息抽屉是否打开
  isMessageDrawerOpen = false;

  // notice订阅句柄
  noticeSub: Subscription;

  // 路由订阅句柄
  routerSub: Subscription;

  // 版权信息
  copyRight = '';

  // 是否收起
  isCollapsed = false;

  // 教学管理地址
  teachingManagementUri = null;

  // 导航模式。在高度不够时，显示为vertical(子菜单弹出显示)
  navMode: 'vertical' | 'horizontal' | 'inline' = 'inline';

  // 是否在阅卷状态
  isMarkStatus: Observable<boolean>;

  // 展示header
  isShowHeader = true;
  // header的固定状态
  headerAffixStatus = false;
  documentTop = 0;
  winResizeSub: Subscription;
  cancel: boolean;
  AiReportTypeEnum = AiReportTypeEnum;

  menuItems: MenuItem[] = [];
  bottomMenuItems: MenuItem[] = [];

  DraggableSnapLockAxis = DraggableSnapLockAxis;

  get isInputSite() {
    return this.sharedSrv.site.editCourse;
  }

  // 是否在选题篮页面
  get isPublishTaskRoute() {
    return this.router.url.indexOf('/exercise/publish/task') > -1;
  }

  /**
   * 是否展示技能控制台
   */
  get isHaveSkillCreator() {
    return (
      !!this.sharedSrv.user?.isOperateInSchool || this.sharedSrv.definePermissionByFeature(PermissionCode.SkillCreator)
    );
  }

  /**
   * alert关闭后，间距不会消失。
   */
  showAlert: boolean = true;

  public sharedSrv = inject(SharedService);
  private siteSrv = inject(SiteService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private modalSrv = inject(NzModalService);
  private checkQuestionSrv = inject(CheckQuestionService);
  private noticeSrv = inject(MessageApiService);
  public baseLayoutService = inject(BaseLayoutService);
  public questionHandleSrv = inject(QuestionHandleService);
  private tokenSrv = inject(TokenService);
  private cdr = inject(ChangeDetectorRef);

  async ngOnInit() {
    this.site = this.sharedSrv.site = await this.siteSrv.getSite();
    this.loadData();
    this.routerSub = this.router.events.subscribe(event => {
      if (event instanceof NavigationStart) {
        this.isMessageDrawerOpen = false;
        // 路由事件回调非模板事件链（程序化导航/后退无点击标脏），消息抽屉开合状态需主动标记
        this.cdr.markForCheck();
      }
    });
    this.route.queryParams.subscribe(params => {
      this.searchValue = params['searchValue'];
      const searchModel = params['searchModel'];
      if (searchModel) {
        this.searchModel = searchModel;
      }
    });
    this.teachingManagementUri = this.teachingManagement();
    // 监听window滚动
    this.subscribeWindowScroll();
    this.selectQuestionCount = await this.checkQuestionSrv.syncRemoteBasket();

    this.checkQuestionSrv.questionSelectedCount.subscribe(count => {
      this.selectQuestionCount = count;
      // 选题篮计数经服务订阅推送（非模板事件链），篮子角标需主动标记
      this.cdr.markForCheck();
    });
    this.initMenu();
    // 菜单/站点 logo/选题篮计数均在 getSite 与 syncRemoteBasket 的 await 续体后写入（ngOnInit 无模板事件链），需主动标记
    this.cdr.markForCheck();
  }

  ngOnDestroy() {
    this.noticeSub?.unsubscribe();
    this.routerSub?.unsubscribe();
  }

  // 监听window滚动
  subscribeWindowScroll() {
    let beforeScrollTop = 0;
    let afterScrollTop = 0;
    this.winResizeSub = fromEvent(window, 'scroll').subscribe(() => {
      // 判断是向上还是向下滚动
      beforeScrollTop = document.documentElement.scrollTop;
      if (afterScrollTop > beforeScrollTop) {
        if (this.isShowHeader) this.isShowHeader = false;
      } else {
        if (beforeScrollTop > 50 && !this.isShowHeader) {
          this.isShowHeader = true;
        }
      }
      this.documentTop = beforeScrollTop;
      setTimeout(() => {
        afterScrollTop = beforeScrollTop;
      }, 0);
    });
  }

  private async loadData() {
    this.site.serverTime = new Date(this.site.serverTime);
    this.logo = this.site.logo;
    const diff = Math.abs(this.site.serverTime.getTime() - new Date().getTime()) / (1000 * 60);
    this.isZhushanSchool = 'zszx.fuulea.com'.indexOf(this.site.name) > -1 && this.site.name && this.site.name !== '';
    // 超过8分钟，提示时间不对
    if (diff > 8) {
      this.modalSrv.error({
        nzTitle: '时间不正确',
        nzContent: '您的系统时间不正确，请纠正您的系统时间',
      });
    }

    // 获得版权认证信息
    this.copyRight = this.sharedSrv.getCopyright();
    // 启动定时器
    this.onGetNotice();
  }

  handleCollapsedState(state) {
    this.baseLayoutService.slidIsCollapsed.next(!state);
  }

  /**
   * 搜索
   */
  onSearch() {
    const url = this.router
      .createUrlTree([`search`], {
        queryParams: {
          searchValue: this.searchValue,
          searchLabel: this.searchModelMap.get(this.searchModel),
          searchModel: this.searchModel,
          pageIndex: 1,
        },
      })
      .toString();
    window.open(url);
  }

  // 是否显示学情仪表
  showDashboard() {
    return (
      this.sharedSrv.user &&
      this.sharedSrv.user.site_id === 1 /* || /^localhost/.test(window.location.hostname) */ &&
      /^(publish|sjzyz1|localhost|192.168.7.233)/.test(window.location.hostname)
    );
  }

  /**
   * 获取通知
   */
  onGetNotice() {
    // 启动全局未读轮询（幂等，单一轮询源），订阅未读数更新菜单角标
    this.noticeSrv.startPolling();
    this.noticeSub = this.noticeSrv.unread$.subscribe(summary => {
      const total = summary.total ?? 0;
      if (total !== this.noticeCount) {
        this.noticeCount = total;
        // 更新消息菜单角标数字
        if (this.bottomMenuItems && this.bottomMenuItems.length) {
          const noticeMenuItem = this.bottomMenuItems.find(item => item.key === 'message');
          if (noticeMenuItem) {
            noticeMenuItem.badge = total;
          }
        }
        this.cdr.detectChanges();
      }
    });
  }

  /**
   * 教学管理
   */
  teachingManagement() {
    const sharedSrv = this.sharedSrv;

    // 年级主任、分站管理员开通教学管理 changed 19.11.2
    if (
      !this.isZhushanSchool &&
      ((sharedSrv.user && sharedSrv.user.isGradeDirector) ||
        // || sharedSrv.user.isSubjectDirector
        (sharedSrv.user && sharedSrv.user.isSuper))
    ) {
      return '/dashboard';
    }

    if (this.isZhushanSchool && sharedSrv.user && sharedSrv.user.isSuper) {
      return '/dashboard/year';
    }

    return null;
  }

  closeAlert() {
    this.showAlert = false;
  }

  initMenu() {
    this.menuItems = [
      {
        label: '课程',
        icon: 'icon-kecheng',
        activeIcon: 'icon-kecheng-xuanzhong',
        route: '/course',
        queryParamsHandling: 'preserve',
        show: true,
      },
      {
        label: '录入',
        icon: 'icon-lurumoren',
        activeIcon: 'icon-luruxuanzhong',
        route: '/input',
        show: this.isInputSite,
        children: [
          {
            label: '教辅分配',
            route: `/input/workbook`,
            show: this.sharedSrv.user.isSuper || this.sharedSrv.user.isOperateInSchool,
          },
          {
            label: '录入工单',
            route: `/input/input-self/${this.sharedSrv.user.user_id}`,
            show: true,
          },
          {
            label: '个人统计',
            route: `/input/input-statis/${this.sharedSrv.user.user_id}`,
            queryParams: { firstName: this.sharedSrv.user.first_name },
            show: this.sharedSrv.user.isSuper || this.sharedSrv.user.keyBoarder,
          },
          {
            label: '团队统计',
            route: `/input/input-team/`,
            queryParams: { firstName: this.sharedSrv.user.first_name },
            show: this.sharedSrv.user.isSuper,
          },
          {
            label: '区校统计',
            route: `/input/input-school`,
            queryParams: { firstName: this.sharedSrv.user.first_name },
            show: this.sharedSrv.user.isSuper,
          },
          {
            label: '课程管理',
            route: `/course/manage`,
            show: this.sharedSrv.user.isSuper,
          },
        ],
      },
      {
        label: '任务',
        icon: 'icon-daohanglan-renwu-moren',
        activeIcon: 'icon-daohanglan-renwu-xuanzhong',
        route: '/task',
        show: true,
      },
      {
        label: '考试',
        icon: 'icon-daohanglan-kaoshi-moren',
        activeIcon: 'icon-daohanglan-kaoshi-xuanzhong',
        route: '/exam',
        show: true,
      },
      {
        label: '题库',
        icon: 'icon-daohanglan-tiku-moren',
        activeIcon: 'icon-daohanglan-tiku-xuanzhong',
        route: '/tk',
        show: true,
      },
      {
        label: '靶向',
        icon: 'icon-daohanglan-tisheng-moren',
        activeIcon: 'icon-daohanglan-tisheng-xuanzhong',
        route: '/target',
        show: true,
        children: [
          { label: '对人靶向', route: '/target/for-student', show: true },
          { label: '对点靶向', route: '/target/for-knowledge', show: true },
        ],
      },
      {
        label: '学情',
        icon: 'icon-daohanglan-xueqing-moren',
        activeIcon: 'icon-daohanglan-xueqing-xuanzhong',
        route: '/analysis',
        show: true,
        children: [
          {
            label: '年级学情',
            route: '/analysis/grade/overview',
            tag: '年级主任',
            show: this.sharedSrv.user.isGradeDirector,
          },
          {
            label: '学科学情',
            route: '/analysis/grade/graph',
            tag: '学科组长',
            show: this.sharedSrv.user.isSubjectDirector,
          },
          {
            label: '班级学情',
            route: '/analysis/class',
            tag: '班主任',
            show:
              this.sharedSrv.user.manageClassroomIds &&
              this.sharedSrv.user.manageClassroomIds.length > 0 &&
              this.sharedSrv.user.teachClassroomIds &&
              this.sharedSrv.user.teachClassroomIds.length > 0,
          },
          {
            label: '班内学科',
            route: '/analysis/class/graph',
            tag: '任课老师',
            show: this.sharedSrv.user.teachClassroomIds && this.sharedSrv.user.teachClassroomIds.length > 0,
          },
        ],
      },
      {
        label: '管理',
        icon: 'icon-daohanglan-jiaoxue-moren',
        activeIcon: 'icon-daohanglan-jiaoxue-xuanzhong',
        route: this.teachingManagementUri,
        show: this.teachingManagementUri,
      },
      {
        label: '数据',
        icon: 'icon-shujumoren',
        activeIcon: 'icon-shujuxuanzhong',
        route: '/bo/dashboard',
        show: this.showDashboard(),
      },
    ];
    // {
    //   label: '豚宝',
    //     icon: 'https://s-chengdu.100tifen.com/miniprogram/static/image/task/avatar.png',
    //   activeIcon: 'https://s-chengdu.100tifen.com/miniprogram/static/image/task/avatar.png',
    //   route: '/algo',
    //   show: this.sharedSrv.defineAiFeaturesByLevel(AiReportTypeEnum.GeneralNew),
    // },

    this.bottomMenuItems = [
      {
        icon: 'icon-daohanglan-sousuo',
        activeColor: '#ffffff',
        color: '#BCC4D3',
        show: true,
        click: () => {
          this.onSearch();
        },
      },
      {
        key: 'message',
        nzIcon: 'bell',
        color: '#BCC4D3',
        activeColor: '#ffffff',
        badge: this.noticeCount,
        show: true,
        // 不设route，点击触发click打开消息抽屉
        click: () => (this.isMessageDrawerOpen = true),
      },
      {
        icon: 'icon-daohanglan-shezhi',
        activeColor: '#ffffff',
        color: '#BCC4D3',
        route: '/setting',
        show: true,
        children: [
          {
            label: '图谱管理',
            route: '/auth/atlas/manage',
            show: this.sharedSrv?.user?.isSuper && this.sharedSrv.user.loginType === LoginTypeEnum.QrCode,
          },
          // 技能控制台仅驻校专家或具备 skill-creator 权限的用户可见，最终权限以后端校验为准。
          {
            label: '技能控制台',
            route: '/algo/skill',
            show: this.isHaveSkillCreator,
          },
          { label: '我的班级', route: '/classroom', show: true },
          {
            label: '用户管理',
            route: '/auth/users',
            show: this.sharedSrv?.user?.isSuper || this.sharedSrv?.user?.isOperateInSchool,
          },
        ],
      },
    ];
    const setting = this.bottomMenuItems.at(-1);
    if (!TabletApp.isTabletApp() && setting.children) {
      setting.children = setting.children.concat([
        { label: '个人资料', route: '/auth/profile', show: true },
        {
          label: '问卷调查',
          route: '/student-survey/home',
          show: !!(
            this.sharedSrv.user?.isSuper ||
            this.sharedSrv.user?.isOperateInSchool ||
            this.sharedSrv.user?.isGradeDirector
          ),
        },
        { label: '登录页设置', route: '/auth/editLogin', show: this.sharedSrv?.user?.isSuper },
        {
          label: '退出登录',
          show: true,
          click: () => {
            this.sharedSrv.clearAll();
            this.tokenSrv.logout(false);
          },
        },
      ]);
    }
  }
}
